import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ENGINES = ["google", "bing", "duckduckgo", "yahoo", "brave", "*"];
const ACTIONS = ["log", "redirect", "block"];
const empty = {
  name: "", engines: ["google", "bing", "duckduckgo"],
  blocked_terms: [], blocked_patterns: [],
  force_safe_search: true, action: "block", enabled: true, description: "",
};

export default function SearchFilter() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [termsText, setTermsText] = useState("");
  const [patsText, setPatsText] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/search-filter/policies"); setItems(data); }
    catch (err) { console.error("search-filter load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const payload = {
      ...form,
      blocked_terms: termsText.split(",").map((t) => t.trim()).filter(Boolean),
      blocked_patterns: patsText.split("\n").map((t) => t.trim()).filter(Boolean),
    };
    try { await api.post("/search-filter/policies", payload); toast.success("Policy created"); setOpen(false); setForm(empty); setTermsText(""); setPatsText(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this search-filter policy?")) return;
    try { await api.delete(`/search-filter/policies/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleEngine = (e) => {
    const has = form.engines.includes(e);
    setForm({ ...form, engines: has ? form.engines.filter((x) => x !== e) : [...form.engines, e] });
  };

  return (
    <Layout title="Search Query Filter" subtitle="OUTBOUND SAFEGUARDS">
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 mb-4 text-xs font-mono text-amber-300">
        ⚠ Control-plane only — proxy hooks for query-param interception not yet wired into rustwall-proxy.
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="sqf-count">
          {items.length} POLICIES · {items.filter((i) => i.enabled).length} ENABLED · {items.filter((i) => i.force_safe_search).length} SAFESEARCH
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sqf-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW POLICY</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Search className="w-4 h-4 text-cyan-400" /> Create Search-Filter Policy</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="sqf-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div className="col-span-2">
                <L>Engines</L>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ENGINES.map((e) => {
                    const on = form.engines.includes(e);
                    return (
                      <button key={e} type="button" onClick={() => toggleEngine(e)}
                        className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-zinc-700 text-zinc-500 hover:text-zinc-300")}>
                        {e}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div><L>Action</L>
                <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                  <SelectTrigger data-testid="sqf-action-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {ACTIONS.map((a) => <SelectItem key={a} value={a} className="font-mono uppercase text-xs">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Force SafeSearch</span>
                <Switch data-testid="sqf-safe-switch" checked={form.force_safe_search} onCheckedChange={(v) => setForm({ ...form, force_safe_search: v })} />
              </div>
              <div className="col-span-2"><L>Blocked terms (comma-separated)</L><Input data-testid="sqf-terms-input" value={termsText} placeholder="exfil, ransomware-as-a-service" onChange={(e) => setTermsText(e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Blocked patterns (one regex per line)</L>
                <textarea data-testid="sqf-patterns-input" value={patsText} onChange={(e) => setPatsText(e.target.value)}
                  placeholder={"\\bcc\\s*number\\b\n\\bssn\\b"} rows={3}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-sm font-mono text-xs p-2 text-zinc-200" />
              </div>
              <div className="col-span-2 flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Enabled</span>
                <Switch data-testid="sqf-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
              <div className="col-span-2"><L>Description</L><Input data-testid="sqf-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-sqf-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sqf-list">
        {items.map((p) => (
          <div key={p.policy_id} data-testid={`sqf-card-${p.policy_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{p.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{p.policy_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " +
                (p.action === "block" ? "text-red-400 border-red-500/40 bg-red-500/10"
                  : p.action === "redirect" ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                  : "text-cyan-400 border-cyan-500/40 bg-cyan-500/10")}>{p.action}</span>
            </div>
            <div className="font-mono text-xs space-y-1 text-zinc-300 mt-2">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">ENGINES</span>{p.engines?.join(" · ")}</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">TERMS</span>{p.blocked_terms?.length || 0} · <span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2 ml-2">REGEX</span>{p.blocked_patterns?.length || 0}</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">SAFESEARCH</span>{p.force_safe_search ? <span className="text-emerald-400">forced</span> : <span className="text-zinc-500">off</span>}</div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className={"text-[10px] font-mono uppercase tracking-widest " + (p.enabled ? "text-emerald-400" : "text-zinc-500")}>{p.enabled ? "ENABLED" : "DISABLED"}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-sqf-${p.policy_id}`} onClick={() => del(p.policy_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no policies.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
